import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import { emphasize } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import React from 'react';
import Select from 'react-select';
import Creatable from 'react-select/creatable';

const PREFIX = 'ChipSelect';

const classes = {
  root: `${PREFIX}-root`,
  input: `${PREFIX}-input`,
  valueContainer: `${PREFIX}-valueContainer`,
  chip: `${PREFIX}-chip`,
  chipFocused: `${PREFIX}-chipFocused`,
  noOptionsMessage: `${PREFIX}-noOptionsMessage`,
  singleValue: `${PREFIX}-singleValue`,
  placeholder: `${PREFIX}-placeholder`,
  paper: `${PREFIX}-paper`,
  divider: `${PREFIX}-divider`,
};

const StyledSelect = styled(Select)(({ theme }) => ({
  [`& .${classes.root}`]: {
    '& .fuse-chip-select__input': {
      color: theme.palette.text.primary,
    },
    '&.standard': {
      '& $placeholder': {},
      '& $valueContainer': {
        paddingTop: 4,
      },
    },
    '&.filled': {
      '& $placeholder': {
        left: 12,
      },
      '& $valueContainer': {
        paddingTop: 24,
        paddingLeft: 12,
      },
      '& $chip': {
        border: '1px solid rgba(0, 0, 0, 0.12)',
      },
    },
    '&.outlined': {
      '& $placeholder': {
        left: 12,
      },
      '& $valueContainer': {
        paddingLeft: 12,
        paddingTop: 12,
      },
    },
  },

  [`& .${classes.input}`]: {
    display: 'flex',
    padding: 0,
    paddingLeft: 10,
    height: 'auto',
  },

  [`& .${classes.valueContainer}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    paddingBottom: 4,
    paddingTop: 12,
    minHeight: 40,
  },

  [`& .${classes.chip}`]: {
    margin: '4px 4px 4px 0',
  },

  [`& .${classes.chipFocused}`]: {
    backgroundColor: emphasize(
      theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08,
    ),
  },

  [`& .${classes.noOptionsMessage}`]: {
    padding: `${theme.spacing()} ${theme.spacing(2)}`,
  },

  [`& .${classes.singleValue}`]: {
    fontSize: 16,
  },

  [`& .${classes.placeholder}`]: {
    position: 'absolute',
    left: 15,
    fontSize: 16,
    margin: 0,
  },

  [`& .${classes.paper}`]: {
    position: 'absolute',
    zIndex: 10,
    marginTop: theme.spacing(),
    left: 0,
    right: 0,
  },

  [`& .${classes.divider}`]: {
    height: theme.spacing(2),
  },
}));

function NoOptionsMessage(props) {
  return (
    <Typography color="textSecondary" className={classes.noOptionsMessage} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

const inputComponent = React.forwardRef((props, ref) => <div ref={ref} {...props} />);

function Control(props) {
  return (
    <TextField
      fullWidth
      className={clsx(classes.root, props.selectProps.textFieldProps.variant)}
      required={props.selectProps.required}
      error={props.selectProps.error}
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          children: props.children,
          ...props.innerProps,
        },
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props) {
  return (
    <MenuItem
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 600 : 400,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <Typography color="textSecondary" className={classes.placeholder} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function SingleValue(props) {
  return (
    <Typography className={classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  return <div className={classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props) {
  const handleDelete = (event) => {
    props.removeProps.onClick();
    props.removeProps.onMouseDown(event);
  };

  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={clsx(
        classes.chip,
        {
          [classes.chipFocused]: props.isFocused,
        },
        props.data.class,
      )}
      onDelete={props.isDisabled ? undefined : handleDelete}
    />
  );
}

function Menu(props) {
  return (
    <Paper square className={classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

function ChipSelect(props) {
  const handleOnChange = (value) => {
    if (value === null) {
      value = [];
    }
    if (props.onChange) {
      props.onChange(value);
    }
  };

  return props.variant === 'outlined' ? (
    <StyledSelect
      classNamePrefix="fuse-chip-select"
      isDisabled={props.disabled ?? false}
      {...props}
      components={components}
      onChange={handleOnChange}
    />
  ) : (
    <Creatable
      isDisabled={props.disabled}
      classNamePrefix="fuse-chip-select"
      {...props}
      components={components}
      onChange={handleOnChange}
    />
  );
}

export default React.memo(ChipSelect);
